import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import {
    BadgeCheck,
    CheckCircle2,
    House,
    MessagesSquare,
    Ruler
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ButtonLink } from '@/components/generals/button'
import { CtaBanner } from '@/components/generals/cta-banner'
import { SectionHeading } from '@/components/generals/section-heading'

const VALUES = [
    {
        icon: MessagesSquare,
        title: "Honest advice",
        text: "We recommend what your home actually needs and what fits your budget, never the most expensive option by default."
    },
    {
        icon: Ruler,
        title: "Precise measuring",
        text: "Every opening is surveyed in person and every frame is made to measure, so it fits properly the first time."
    },
    {
        icon: House,
        title: "Respect for your home",
        text: "Dust sheets down, mess tidied away and old frames removed at the end of every working day."
    },
    {
        icon: BadgeCheck,
        title: "Built to last",
        text: "Quality frames, glazing and hardware, backed by a 10-year guarantee and our own aftercare."
    }
]

const PROMISES = [
    "A free home survey and honest design advice",
    "Clear, written quotes with no hidden extras",
    "No pushy sales tactics, ever",
    "Careful installation by trained fitters",
    "Old frames removed and recycled wherever possible",
    "A 10-year guarantee on frames, glass and hardware"
]

const AboutPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="About us"
                description="VIONA makes replacing windows and doors simple, with honest advice, made-to-measure UPVC products and careful, tidy installation."
            />

            <section id='about-hero' aria-labelledby='about-hero-heading' className='border-b border-border bg-secondary/50'>
                <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24'>
                    <p className='text-xs font-semibold uppercase tracking-[0.18em] text-brand-strong'>
                        About VIONA
                    </p>

                    <h1 id='about-hero-heading' className='mt-3 max-w-3xl text-balance font-heading text-4xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl'>
                        Better windows and doors, fitted with care.
                    </h1>

                    <p className='mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-muted-foreground'>
                        We believe replacing your windows and doors should feel easy, and that the result should keep your home warm, quiet and secure for decades.
                    </p>
                </div>
            </section>

            <section id='our-approach' aria-labelledby='our-approach-heading' className='mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28'>
                <div className='grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-20'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-2xl bg-secondary sm:aspect-[4/3] lg:aspect-[4/5]'>
                        <Image
                            src='/images/about.jpg'
                            alt="Close-up of an old timber window with peeling paint and a worn brass catch"
                            fill
                            sizes='(min-width: 1024px) 45vw, 100vw'
                            className='object-cover'
                        />
                    </div>

                    <div>
                        <SectionHeading
                            eyebrow="Why we do it"
                            title="Old windows cost more than you think"
                            headingId='our-approach-heading'
                        />

                        <div className='mt-6 space-y-5 text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg'>
                            <p>
                                Draughty frames, misted glass, sticking sashes and peeling paint quietly chip away at the comfort of a home, and at your heating bills. Replacing them is one of the most noticeable improvements you can make, yet the process can feel daunting.
                            </p>

                            <p>
                                VIONA exists to make it simple. We listen first, recommend only what your home needs and give you a clear, fixed price. Then we fit everything with the same care we would want in our own homes, and take the old frames away for recycling.
                            </p>
                        </div>

                        <ButtonLink href='/products' variant='outline' className='mt-8'>
                            Explore our products
                        </ButtonLink>
                    </div>
                </div>
            </section>

            <section id='values' aria-labelledby='values-heading' className='bg-secondary/50'>
                <div className='mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28'>
                    <SectionHeading
                        eyebrow="Our values"
                        title="What you can expect from us"
                        align='center'
                        headingId='values-heading'
                    />

                    <ul className='mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                        {VALUES.map(({ icon: Icon, title, text }) => (
                            <li key={title} className='rounded-xl border border-border bg-card p-7'>
                                <span className='flex size-11 items-center justify-center rounded-lg bg-brand/20 text-brand-strong'>
                                    <Icon aria-hidden='true' className='size-5' />
                                </span>

                                <h3 className='mt-5 font-heading text-lg font-semibold text-foreground'>
                                    {title}
                                </h3>

                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    {text}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='our-promise' aria-labelledby='our-promise-heading' className='bg-ink text-ink-foreground'>
                <div className='mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:items-center lg:gap-20 lg:py-28'>
                    <SectionHeading
                        eyebrow="The VIONA promise"
                        title="Six things we never compromise on"
                        description="Whether you are replacing a single front door or every window in the house, you get the same standard of service."
                        tone='inverted'
                        headingId='our-promise-heading'
                    />

                    <ul className='grid gap-4 sm:grid-cols-2'>
                        {PROMISES.map(promise => (
                            <li key={promise} className='flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-5 text-sm leading-relaxed text-ink-foreground'>
                                <CheckCircle2 aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-brand' />
                                {promise}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <div className='pt-20 lg:pt-28'>
                <CtaBanner />
            </div>
        </Layout>
    )
}

export default AboutPage
